import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useTheme } from '../contexts/ThemeContext.tsx';
import {
  HomeIcon,
  FolderIcon,
  UserGroupIcon,
  Bars3Icon,
  XMarkIcon,
  CogIcon,
  BellIcon,
  ChartBarIcon,
  ChevronDownIcon,
  CreditCardIcon,
  TruckIcon,
  ClipboardDocumentListIcon,
  ClipboardIcon,
  ClipboardDocumentCheckIcon,
  AdjustmentsVerticalIcon,
  DocumentCurrencyRupeeIcon,
  SunIcon,
  MoonIcon,
  ArrowRightOnRectangleIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { useSync } from '../contexts/SyncContext.tsx';
import { Toast } from './Toast.tsx';
import { odooConfigAPI } from '../services/api.ts';

const FuelIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth="1.5"
    stroke="currentColor"
    className="h-6 w-6"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z"
    />
  </svg>
);

const MaintenanceIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth="1.5"
    stroke="currentColor"
    className="h-6 w-6"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M11.42 15.17 17.25 21A2.652 2.652 0 0 0 21 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 1 1-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 0 0 4.486-6.336l-3.276 3.277a3.004 3.004 0 0 1-2.25-2.25l3.276-3.276a4.5 4.5 0 0 0-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437 1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008Z"
    />
  </svg>
);

const TollIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth="1.5"
    stroke="currentColor"
    className="h-6 w-6"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Z"
    />
  </svg>
);

const PersonalUseIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth="1.5"
    stroke="currentColor"
    className="h-6 w-6"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M18 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0ZM3 19.235v-.11a6.375 6.375 0 0 1 12.75 0v.109A12.318 12.318 0 0 1 9.374 21c-2.331 0-4.512-.645-6.374-1.766Z"
    />
  </svg>
);

const DailyActivityIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth="1.5"
    stroke="currentColor"
    className="h-6 w-6"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5m-9-6h.008v.008H12v-.008ZM12 15h.008v.008H12V15Zm0 2.25h.008v.008H12v-.008ZM9.75 15h.008v.008H9.75V15Zm0 2.25h.008v.008H9.75v-.008ZM7.5 15h.008v.008H7.5V15Zm0 2.25h.008v.008H7.5v-.008Zm6.75-4.5h.008v.008h-.008v-.008Zm0 2.25h.008v.008h-.008V15Zm0 2.25h.008v.008h-.008v-.008Zm2.25-4.5h.008v.008H16.5v-.008Zm0 2.25h.008v.008H16.5V15Z"
    />
  </svg>
);

type NavigationItem = {
  name: string;
  href: string;
  icon: React.FC<React.SVGProps<SVGSVGElement>>;
  isDropdown?: boolean;
  dropdown?: NavigationItem[];
};

const getNavigation = (role: string): NavigationItem[] => {
  const baseNav: NavigationItem[] = [
    { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
    {
      name: 'Advertisement',
      href: '/advertisement',
      icon: AdjustmentsVerticalIcon,
    },
  ];

  if (role === 'admin') {
    return [
      ...baseNav,
      { name: 'Users', href: '/employees', icon: UserGroupIcon },
      { name: 'Manage Notifications', href: '/notifications', icon: BellIcon },
      { name: 'Configuration', href: '/configuration', icon: CogIcon },
      { name: 'Settings', href: '/settings', icon: AdjustmentsVerticalIcon },
      { name: 'Sales', href: '/sales', icon: ChartBarIcon },
      {
        name: 'Fleet Management',
        href: '#',
        icon: TruckIcon,
        isDropdown: true,
        dropdown: [
          { name: 'Fuel', href: '/fleet/fuel', icon: FuelIcon },
          {
            name: 'Maintenance',
            href: '/fleet/maintenance',
            icon: MaintenanceIcon,
          },
          { name: 'Toll', href: '/fleet/toll', icon: TollIcon },
          {
            name: 'Personal Use',
            href: '/fleet/personal-use',
            icon: PersonalUseIcon,
          },
        ],
      },
      {
        name: 'Daily Activity',
        href: '/daily-activity',
        icon: DailyActivityIcon,
      },
      {
        name: 'Credit Limit',
        href: '#',
        icon: CreditCardIcon,
        isDropdown: true,
        dropdown: [
          {
            name: 'Credit Limit Apply',
            href: '/credit-limit-apply',
            icon: ClipboardDocumentListIcon,
          },
          {
            name: 'Credit Limit Extension',
            href: '/credit-limit-extension',
            icon: ClipboardIcon,
          },
          {
            name: 'Temporary Credit',
            href: '/temporary-credit',
            icon: ClipboardDocumentCheckIcon,
          },
        ],
      },
      {
        name: 'Expense',
        href: '/expense',
        icon: DocumentCurrencyRupeeIcon,
      },
      {
        name: 'Reports',
        href: '/reports',
        icon: DocumentTextIcon,
      },
    ];
  }

  if (role === 'super_admin') {
    return [
      ...baseNav,
      { name: 'Projects', href: '/projects', icon: FolderIcon },
      { name: 'Sales', href: '/sales', icon: ChartBarIcon },
      {
        name: 'Odoo Configurations',
        href: '/odoo-configurations',
        icon: CogIcon,
      },
      {
        name: 'Settings',
        href: '/settings',
        icon: AdjustmentsVerticalIcon,
      },
      {
        name: 'Fleet Management',
        href: '#',
        icon: TruckIcon,
        isDropdown: true,
        dropdown: [
          { name: 'Fuel', href: '/fleet/fuel', icon: FuelIcon },
          {
            name: 'Maintenance',
            href: '/fleet/maintenance',
            icon: MaintenanceIcon,
          },
          { name: 'Toll', href: '/fleet/toll', icon: TollIcon },
          {
            name: 'Personal Use',
            href: '/fleet/personal-use',
            icon: PersonalUseIcon,
          },
        ],
      },
      {
        name: 'Daily Activity',
        href: '/daily-activity',
        icon: DailyActivityIcon,
      },
      {
        name: 'Credit Limit',
        href: '#',
        icon: CreditCardIcon,
        isDropdown: true,
        dropdown: [
          {
            name: 'Credit Limit Apply',
            href: '/credit-limit-apply',
            icon: ClipboardDocumentListIcon,
          },
          {
            name: 'Credit Limit Extension',
            href: '/credit-limit-extension',
            icon: ClipboardIcon,
          },
          {
            name: 'Temporary Credit',
            href: '/temporary-credit',
            icon: ClipboardDocumentCheckIcon,
          },
        ],
      },

      {
        name: 'Expense',
        href: '/expense',
        icon: DocumentCurrencyRupeeIcon,
      },
      {
        name: 'Reports',
        href: '/reports',
        icon: DocumentTextIcon,
      },
    ];
  }

  return baseNav;
};

export const Layout: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, project, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { syncStatus, isSyncing, startSync, updateSync, endSync } = useSync();
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  const [dropdownOpen, setDropdownOpen] = useState<number[]>([]); // Using an array to track open dropdowns
  const navigation = getNavigation(user?.role || '');
  const configId = project?.odoo_configuration_id;

  const BrandingComponent = () => (
    <>
      {user?.role === 'admin' && project ? (
        <div className="flex items-center gap-2">
          {project.logo ? (
            <img
              src={`${project.logo}`}
              alt={project.name}
              className="h-8 w-8 object-contain"
            />
          ) : null}
          <span className="text-xl font-semibold">{project.name}</span>
        </div>
      ) : (
        <span className="text-xl font-semibold">Admin Panel</span>
      )}
    </>
  );

  const handleLogout = async () => {
    try {
      if (isSyncing) {
        setToast({
          message: 'Please wait until the sync is completed.',
          type: 'error',
        });
        return;
      }

      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleSync = () => {
    if (!configId || isSyncing) return;
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Get the stream URL - uses Lambda Function URL if STREAM_API_URL is set
      let url = odooConfigAPI.getAuthorizationStream(configId);

      // Add token as query parameter
      const separator = url.includes('?') ? '&' : '?';
      url = `${url}${separator}token=${token}`;

      // Begin syncing with an initial message
      startSync('Connecting to Odoo...');

      // Create the EventSource to listen to server-sent events
      const eventSource = new EventSource(url);

      // When a message is received, update the sync message
      eventSource.onmessage = (event) => {
        const data = event.data;

        // Check if this is the completion message
        try {
          const parsed = JSON.parse(data);
          if (parsed.type === 'complete') {
            setToast({
              message: parsed.success
                ? 'Data synced successfully'
                : `Sync completed with ${parsed.errors} errors`,
              type: parsed.success ? 'success' : 'error',
            });
            eventSource.close();
            endSync();
            return;
          }
        } catch {
          // Not JSON, just a status message
        }

        updateSync(data);
      };

      // Handle errors from the event stream
      eventSource.onerror = (error) => {
        console.error('Sync error:', error);
        setToast({
          message: 'Failed to connect to sync stream',
          type: 'error',
        });
        eventSource.close();
        endSync();
      };
    } catch (error: any) {
      console.error('Sync error:', error);
      setToast({ message: error.message, type: 'error' });
      endSync();
    }
  };

  const toggleDropdown = (index: number) => {
    setDropdownOpen((prevState) => {
      // Check if the dropdown is already open, if so, remove it from the array
      if (prevState.includes(index)) {
        return prevState.filter((i) => i !== index);
      } else {
        // Otherwise, add it to the array
        return [...prevState, index];
      }
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      <div
        className={`fixed inset-0 z-40 lg:hidden ${
          sidebarOpen ? '' : 'hidden'
        }`}
      >
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75"
          onClick={() => setSidebarOpen(false)}
        />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-white dark:bg-gray-800 shadow-xl">
          <div className="flex h-16 items-center justify-between px-4 border-b border-gray-200 dark:border-gray-700">
            <div className="text-gray-900 dark:text-white">
              <BrandingComponent />
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 space-y-1 px-3 py-4 overflow-y-auto">
            {navigation.map((item, index) => {
              if (item?.isDropdown as boolean) {
                const isDropdownActive = dropdownOpen.includes(index);
                const isChildActive = item.dropdown?.some((sub) =>
                  location.pathname.startsWith(sub.href),
                );

                return (
                  <div key={item.name} className="space-y-1">
                    <button
                      onClick={() => toggleDropdown(index)}
                      className={`group flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200 ${
                        isChildActive
                          ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50 hover:text-gray-900 dark:hover:text-white'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <item.icon
                          className={`h-3 w-3 flex-shrink-0 ${
                            isChildActive
                              ? 'text-primary-600 dark:text-primary-400'
                              : 'text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-white'
                          }`}
                        />
                        <span>{item.name}</span>
                      </div>
                      <ChevronDownIcon
                        className={`h-4 w-4 transition-transform duration-200 ${
                          isDropdownActive ? 'rotate-180' : ''
                        } ${
                          isChildActive
                            ? 'text-primary-600 dark:text-primary-400'
                            : 'text-gray-400 group-hover:text-gray-500'
                        }`}
                      />
                    </button>

                    {isDropdownActive && (
                      <div className="mt-1 space-y-1 pl-11 pr-2">
                        {item.dropdown?.map((subItem) => {
                          const isSubActive = location.pathname.startsWith(
                            subItem.href,
                          );
                          return (
                            <Link
                              key={subItem.name}
                              to={subItem.href}
                              className={`group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200 ${
                                isSubActive
                                  ? 'text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/10'
                                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700/30'
                              }`}
                            >
                              <subItem.icon
                                className={`h-4 w-4 flex-shrink-0 ${
                                  isSubActive
                                    ? 'text-primary-600 dark:text-primary-300'
                                    : 'text-gray-500 dark:text-gray-500'
                                }`}
                              />
                              <span>{subItem.name}</span>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              const isActive = location.pathname.startsWith(item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  <item.icon
                    className={`h-5 w-5 flex-shrink-0 ${
                      isActive
                        ? 'text-white'
                        : 'text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-white'
                    }`}
                  />
                  <span>{item.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* User Profile & Logout */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 mt-auto bg-gray-50/50 dark:bg-gray-800/50">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white font-bold shadow-sm">
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                  {user?.name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate capitalize">
                  {user?.role?.replace('_', ' ') || 'User'}
                </p>
              </div>
            </div>
            {user?.role === 'admin' && configId && (
              <button
                onClick={handleSync}
                className="w-full flex items-center justify-center gap-2 rounded-xl border border-gray-200 dark:border-gray-600 bg-primary text-white px-3 py-2 text-sm font-medium hover:bg-primary-dark transition-all duration-200 shadow-sm mb-2 disabled:opacity-50"
                disabled={isSyncing}
                title="Sync company data"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
                  />
                </svg>
                <span>{isSyncing ? 'Syncing...' : 'Sync Data'}</span>
              </button>
            )}
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-red-600 dark:hover:text-red-400 transition-all duration-200 shadow-sm"
            >
              <ArrowRightOnRectangleIcon className="h-4 w-4" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex min-h-0 flex-1 flex-col bg-white dark:bg-gradient-to-b dark:from-gray-900 dark:to-gray-800 shadow-xl border-r border-gray-200 dark:border-gray-700">
          {/* Logo/Branding */}
          <div className="flex h-16 items-center px-4 border-b border-gray-200 dark:border-gray-700">
            <div className="text-gray-900 dark:text-white">
              <BrandingComponent />
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-1 px-3 py-4 overflow-y-auto">
            {navigation.map((item, index) => {
              if (item.isDropdown) {
                const isAnySubItemActive = item.dropdown?.some((subItem) =>
                  location.pathname.startsWith(subItem.href),
                );

                return (
                  <div key={item.name} className="mb-1">
                    <button
                      onClick={() => toggleDropdown(index)}
                      className={`w-full group flex items-center justify-between rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200 ${
                        isAnySubItemActive
                          ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50 hover:text-gray-900 dark:hover:text-white'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {item.icon && (
                          <item.icon
                            className={`h-5 w-5 flex-shrink-0 ${
                              isAnySubItemActive
                                ? 'text-white'
                                : 'text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-white'
                            }`}
                          />
                        )}
                        <span>{item.name}</span>
                      </div>
                      <ChevronDownIcon
                        className={`h-4 w-4 transition-transform duration-200 ${
                          dropdownOpen.includes(index) ? 'rotate-180' : ''
                        } ${
                          isAnySubItemActive
                            ? 'text-white'
                            : 'text-gray-500 dark:text-gray-400'
                        }`}
                      />
                    </button>

                    {/* Dropdown Items */}
                    <div
                      className={`overflow-hidden transition-all duration-200 ${
                        dropdownOpen.includes(index)
                          ? 'max-h-96 opacity-100 mt-1'
                          : 'max-h-0 opacity-0'
                      }`}
                    >
                      <div className="ml-4 space-y-1 border-l-2 border-gray-200 dark:border-gray-700 pl-3">
                        {item.dropdown?.map((subItem) => {
                          const isSubActive = location.pathname.startsWith(
                            subItem.href,
                          );
                          return (
                            <Link
                              key={subItem.name}
                              to={subItem.href}
                              className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200 ${
                                isSubActive
                                  ? 'bg-primary-50 dark:bg-primary-500/20 text-primary-700 dark:text-primary-300 border-l-2 border-primary-500 dark:border-primary-400 -ml-[2px] pl-[10px]'
                                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/30 hover:text-gray-900 dark:hover:text-gray-200'
                              }`}
                            >
                              <subItem.icon
                                className={`h-4 w-4 flex-shrink-0 ${
                                  isSubActive
                                    ? 'text-primary-600 dark:text-primary-300'
                                    : 'text-gray-500 dark:text-gray-500'
                                }`}
                              />
                              <span>{subItem.name}</span>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              }

              const isActive = location.pathname.startsWith(item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  <item.icon
                    className={`h-5 w-5 flex-shrink-0 ${
                      isActive
                        ? 'text-white'
                        : 'text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-white'
                    }`}
                  />
                  <span>{item.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* User Profile & Logout */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 mt-auto bg-gray-50/50 dark:bg-gray-800/50">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white font-bold shadow-sm">
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                  {user?.name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate capitalize">
                  {user?.role?.replace('_', ' ') || 'User'}
                </p>
              </div>
            </div>
            {user?.role === 'admin' && configId && (
              <button
                onClick={handleSync}
                className="w-full flex items-center justify-center gap-2 rounded-xl border border-gray-200 dark:border-gray-600 bg-primary text-white px-3 py-2 text-sm font-medium hover:bg-primary-dark transition-all duration-200 shadow-sm mb-2 disabled:opacity-50"
                disabled={isSyncing}
                title="Sync company data"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
                  />
                </svg>
                <span>{isSyncing ? 'Syncing...' : 'Sync Data'}</span>
              </button>
            )}
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-red-600 dark:hover:text-red-400 transition-all duration-200 shadow-sm"
            >
              <ArrowRightOnRectangleIcon className="h-4 w-4" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col lg:pl-64 min-h-screen">
        {/* Top header */}
        <div className="sticky top-0 z-10 flex h-16 flex-shrink-0 bg-white dark:bg-gray-800 shadow-sm backdrop-blur-sm bg-opacity-75 dark:bg-opacity-90 border-b border-gray-200 dark:border-gray-700">
          <button
            type="button"
            className="px-4 text-gray-500 dark:text-gray-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
          <div className="flex flex-1 justify-between px-4">
            <div className="flex flex-1"></div>
            <div className="ml-4 flex items-center md:ml-6 gap-3">
              {/* Theme Toggle Button */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-200"
                aria-label="Toggle theme"
              >
                {theme === 'light' ? (
                  <MoonIcon className="h-5 w-5" />
                ) : (
                  <SunIcon className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Main content area with custom scrollbar */}
        <main className="flex-1 overflow-auto bg-gray-50 dark:bg-gray-900">
          <div className="mx-auto max-w-full px-4 sm:px-6 py-6">
            {children}

            {/* Sync Status Display */}
            {isSyncing && (
              <div className="fixed bottom-0 left-0 right-0 bg-gray-800 text-white p-4">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                  <div className="flex items-center">
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span>{syncStatus}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Toast */}
      {toast && (
        <Toast
          type="error"
          message="Please wait until the sync is completed."
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};
